import { Params } from './search-params';

interface AssignedFeature {
    FeatureID: number,
    CandidateID: string,
    Name: string,
    Enabled: boolean,
    Variant: string,
    LastModified: string
}

export interface Application {
    Status: string,
    FeatureEnabledStatus: string,
    Archived: boolean | null,
    DateSubmitted: string | null
}

interface ProfileEligibility {
    IsVeteran: boolean,
    FederalCode: string,
    ServiceCode: string,
    IsCTAPEligible: boolean,
    IsICTAPEligible: boolean,
    TravelPercentage: string,
    ProfileUpToDate: boolean,
    WantsRelocation: boolean,
    WantsTelework: boolean
}

interface ProfilePreferences {
    IsVRA: boolean,
    IsDisVet30: boolean,
    IsDisVetTr: boolean,
    IsMilSpouse: boolean,
    IsFormerOverseas: boolean,
    IsSchedA: boolean,
    PositionOfferings: string[],
    PositionSchedules: string[],
    DesiredLocations: string[],
    TravelPercentage: string[]
}

export interface HiringPath {
    HiringPathId: number,
    Code: string,
    Tooltip: string,
    SearchDisplay: string
}

export interface UserObject {
    FirstName: string,
    FullName: string,
    NameAbbreviation: string,
    AnalyticsID: string,
    AuthenticationAttribution?: AuthenticationAttribution,
    IsAuthenticated: boolean,
    AssignedFeatures: AssignedFeature[],
    LastLoad: number,
    SavedJobs: { [key: string]: boolean },
    AppliedJobs: { [key: string]: Application },
    FederalCode?: string,
    SearchResults: UserObjectSearchResults,
    UniqueSearch: {
        Id: string,
        Keyword: string | undefined,
        Page: string | undefined,
        filters: {
            filterId: string,            
        },
        total_results: number | undefined
    },
    DefaultFilters?: Params,
    UseDefaultFilters?: boolean,
    HasAvailableTalentNetworks:boolean
}

export interface AuthenticationAttribution {
    IsAccountCreated: boolean,
    UtmID?: string,
    UtmSource?: string,
    UtmMedium?: string,
    UtmCampaign?: string,
    UtmTerm?: string,
    UtmContent?: string,
    LandingPath?: string,
    ReferrerHost?: string
}

export interface UserObjectSearchResults {
    Params: Params,
    Results: {
        Items: string[],
        ResultCount: number,
        ReturnUrl: string,
        PageSets: { Page: number, PageItems: string[] }[]
    },
    SelectedDocumentId: string,
    ConversionId: string
}

export interface UsajConfig {
    DataHome: string,
    MediaHome: string,
    BaseUrl: string,
    IdleTimeout: number
}

export interface DocumentData {
    id: string,
    name: string,
    type: string,
    isResume: boolean
}

export interface KeyboardKey {
    keyCode: number;
    keyName: string;
}

export interface AutocompleteResult {
    ParentName: string | null;
    Name: string;
    Code: string;
    Acronym: string;
    SearchText: string;
    Type: string
}

export interface AutocompleteResults {
    [key: string]: AutocompleteResult[];
}

export interface AutocompleteListState {
    id: string,
    length: number
}

export interface CEQuestion {
    questionID: number,
    questionText: string,
}

export interface CEQuestionResponse {
    questionID: number,
    responseValue: number
}

export interface CEQuizResponse {
    currentQuestionIndex: number,
    resultsPageIndex: number,
    responseId: number,
    responses: CEQuestionResponse[]
}

export interface CEJobSeries {
    id: number,
    seriesCode: string,
    seriesName: string,
    description: string,
    questionValues: number[],
    relatedJobTitles: string[],
    correlationScore: number
}

export interface MapDataAttributes {
    latitude: number,
    longitude: number,
    locationText: string
}

export interface Job {
    Title: string;
    Agency: string;
    SubAgency: string;
    Department: string;
    SalaryDisplay: string;
    DocumentID: string;
    PositionID: string;
    Location: string;
    LocationDisplay: string;
    DateDisplay: string;
    ClockDisplay: ClockDisplay;
    WorkSchedule: string;
    WorkType: string;
    HiringPath: HiringPath[];
    Series: string,
    Office: string,
    JobGrade: string,
    LowGrade: string,
    HighGrade: string,
    JobCategoryCode: JobCategory[],
    PositionStartDate: string,
    PositionEndDate: string,
    AnnouncementClosingType: string,
    AnnouncementClosingTypeOption: string
}

export interface JobCategory {
    Name: string,
    Code: string
}

export interface Filters {
    FilterType: string;
    Items: Filter[];
}

export interface Filter {
    Value: string;
    Name: string;
    Count: string;
    Selected: boolean;
    Tooltip: string;
    GroupText: string;
}

export interface SelectedFilter {
    Name: string;
    Value: string;
    FilterType: string;
    IsBrandedSearch: boolean;
}

export interface SearchResults {
    IsRadialSearch: boolean;
    IsValid: boolean;
    Jobs: Job[];
    Total: number;
    Pages: number;
    Pager: Pager;
    Filters: Filters;
    f: any;
    Suggestion: string;
    SortField: string;
    TeaserName: string;
}

export const saveJobStatuses = {
    saved: 'Saved',
    deleted: 'Deleted',
    maximumReached: 'MaximumReached'
} as const;

export type SaveJobStatus = typeof saveJobStatuses[keyof typeof saveJobStatuses];

export interface SaveJobResponse {
    Status: SaveJobStatus;
    Message?: string;
}

export interface Pager {
    CurrentPageIndex: number;
    FirstPageIndex: number;
    HasNextPage: boolean;
    HasPreviousPage: boolean;
    ItemsPerPage: number;
    LastPageIndex: number;
    NextPageIndex: number;
    NumberOfItems: number;
    NumberOfPages: number;
    PreviousPageIndex: number;
}

export interface ACClickEventDetails {
    Type: string,
    Name: string,
    Code: string,
    ParamProperty: string
}

export interface ComboboxElement {
    select: HTMLSelectElement,
    input: HTMLInputElement,
    toggle: HTMLButtonElement,
    clear: HTMLButtonElement,
    list: HTMLUListElement
}

export interface DataLayerSearch {
    event: string,
    searchId: string,
    authenticated: boolean,
    search_term: string,
    filters: { filterId: string },
    total_results: number | undefined,
    autocomplete: boolean
}

export interface DataLayerSearchFilter extends DataLayerSearch {
    filter_added: boolean;
    filter_removed: boolean;
    filters_changed: string[];
}

export interface alertBannerOptions {
    title?: string,
    alertType: 'Info' | 'Success' | 'Error' | 'Warning' | 'Emergency',
    hideIcon?: boolean,
    isDismissable?: boolean,
    headingLevel?: number,
    message: string
}

export interface AgencyInfo {
    name: string,
    code: string
}

export interface OccupationAggregate {
    agencies: AgencyInfo[];
    positionCount: number;
    totalAgencyCount: number;
}

const clockDisplayValues = {
    closingIn7Days: 'JobClosingIn7Days',
    closingIn3Days: 'JobClosingIn3Days',
    closed: 'JobClosed',
    default: ''
} as const;

export type ClockDisplay = typeof clockDisplayValues[keyof typeof clockDisplayValues];
